import { Field, Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import { ProcessTypeEnum } from '../../../constants/enums';
import validAllowedCharacters from '../../../constants/valid-character-password';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IAddEditFormProps } from '../../../models/forms/IForm';
import { IActionFormatter, IReactSelectOption } from '../../../models/ICommon';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormTextArea from '../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import { ProcessTemplateValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import { formatMessage } from '../../../translations/format-message';

interface IProps extends IAddEditFormProps<any> {
  allProcessesOptions: IReactSelectOption[];
  allProcess: any[];
}

const customStyles = {
        control: (base) => ({
          ...base,
          // This line disable the blue border
          boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
          borderRadius: '8px',
          minHeight: '46px',
          border: '1px solid var(--bs-border-color)',
        }),

        option: (base, { isFocused, isSelected }) => {
          return {
            ...base,
            backgroundColor: isFocused ? '#33439c' : isSelected ? '#33439c' : null, // Light blue on hover, darker blue if selected
            color: isFocused ? '#fff' : isSelected ? '#fff' : '#000', // Black text on hover/selected, dark grey otherwise
          };
        },
      };

const AddEditProcessTemplateForm = (props: IProps) => {
  const TEMPLATE_NAME_MAX_LENGTH = 256;
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isDueDatesValid, setIsDueDatesValid] = useState<boolean>(true);
  const [selectedProcessesCount, setSelectedProcessesCount] = useState(0);
  const [selectedProcesses, setSelectedProcesses] = useState<any[]>([]);
  const [selectedProcessesOptions, setSelectedProcessesOptions] = useState<
    any[]
  >([]);
  const processHeaders = [
    // 'Id',
    'process_name',
    'short_name',
    'process_template_due_date_header_text',
    'move',
  ];
  const formikRef = useRef(null);

  // useEffect(() => {
  //   if (selectedProcesses?.length) {
  //     bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [selectedProcesses]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        props.initialValues.ProcesTemplates.length
      );
    }
  }, [props.initialValues.ProcesTemplates]);

  useEffect(() => {
    setSelectedProcessesOptions([...props.allProcessesOptions]);
  }, [props.allProcessesOptions]);

  useEffect(() => {
    // Check if props.initialValue and props.initialValue.ProcessTemplate are valid
    if (props.initialValues?.ProcesTemplates) {
      setSelectedProcesses(props.initialValues.ProcesTemplates);
    }
  }, [props.initialValues?.ProcesTemplates]);

  const moveProcessUpHandler = (index: any) => {
    if (index === 0) return;
    const newList = [...selectedProcesses];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setSelectedProcesses([...newList]);
  };

  const moveProcessDownHandler = (index: number) => {
    if (index === selectedProcesses.length - 1) return;
    const newList = [...selectedProcesses];
    [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
    setSelectedProcesses([...newList]);
  };

  const handleSelectChange = async (option: any) => {
    const myProcess = props.allProcess.find(
      (p, i) => p.Id === Number(option.value)
    );
    setSelectedProcesses((prev) => {
      const isProcessExist = prev.find((p) => {
        return p.ProcessId === myProcess.Id;
      });

      if (!isProcessExist) {
        return [
          ...selectedProcesses,
          {
            Id: 0,
            ProcessId: myProcess.Id,
            ProcessName: myProcess.ProcessName,
            ShortName: myProcess.ShortName,
            DueDates: 0,
            ProcessOrder: selectedProcesses.length + 1,
          },
        ];
      }

      return prev;
    });

    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        selectedProcesses.length + 1
      ); // Set value of numberInput to 10
    }
  };

  const removeProcessHandler = (index: any) => {
    const newList = selectedProcesses.filter((_, i) => i !== index);
    setSelectedProcesses(newList);
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        selectedProcesses.length - 1
      ); // Set value of numberInput to 10
    }
  };

  const validateDueDates = (processes: any[]): boolean => {
    return processes.every((process, index) => {
      if (index === 0) return true; // Skip the first process
      return process.DueDates <= processes[index - 1].DueDates;
    });
  };

  useEffect(() => {
    setIsDueDatesValid(validateDueDates(selectedProcesses));
  }, [selectedProcesses]);

  const actionIcons: IActionFormatter[] = [
    {
      dataField: 'up',
      handler: moveProcessUpHandler,
      icon: (
        <svg
          version="1.1"
          id="Capa_1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 466.205 466.205"
          fill="currentColor"
          width="16px"
        >
          <g>
            <g>
              <path
                d="M416.193,170.894c-0.052-0.053-0.105-0.105-0.158-0.158L253.049,7.536c-1.295-1.288-2.726-2.432-4.267-3.413l-4.693-2.56
			l-3.2-1.067h-2.347c-3.596-0.661-7.283-0.661-10.88,0h-2.133h-2.56l-3.627,1.92c-2.021,1.098-3.887,2.461-5.547,4.053
			L50.169,170.736c-10.957,10.87-11.028,28.565-0.158,39.522c0.052,0.053,0.105,0.105,0.158,0.158
			c11.045,10.538,28.421,10.538,39.467,0l97.493-97.28c4.206-4.124,10.96-4.058,15.084,0.149c1.919,1.957,3.011,4.577,3.049,7.318
			v317.653c-0.001,15.435,12.51,27.948,27.945,27.949c15.435,0.001,27.947-12.51,27.949-27.945c0-0.001,0-0.003,0-0.004V120.603
			c0.083-5.89,4.925-10.599,10.815-10.516c2.74,0.039,5.361,1.13,7.318,3.049l97.067,97.28c11.073,10.674,28.607,10.674,39.68,0
			C426.993,199.546,427.064,181.851,416.193,170.894z"
              />
            </g>
          </g>
        </svg>
      ),
      isDisable: false,
      title: 'up',
      action: 'up',
      varient: 'primary',
    },
    {
      dataField: 'down',
      handler: moveProcessDownHandler,
      icon: (
        <svg
          version="1.1"
          id="Capa_1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 466.205 466.205"
          fill="currentColor"
          width="16px"
        >
          <g>
            <g>
              <path
                d="M416.17,257.013c-0.052-0.053-0.105-0.105-0.158-0.158c-11.045-10.538-28.421-10.538-39.467,0l-97.28,97.28
			c-4.206,4.124-10.96,4.058-15.084-0.149c-1.919-1.957-3.011-4.577-3.049-7.318V27.949C261.133,12.514,248.622,0.001,233.188,0
			c-15.435-0.001-27.948,12.51-27.949,27.945c0,0.001,0,0.003,0,0.004v317.653c-0.083,5.89-4.925,10.599-10.815,10.516
			c-2.74-0.038-5.361-1.13-7.318-3.049l-97.28-97.28c-10.957-10.957-28.723-10.957-39.68,0c-10.957,10.957-10.957,28.723,0,39.68
			l163.2,163.2c1.295,1.288,2.726,2.432,4.267,3.413l4.693,2.56l3.2,1.067h2.347c3.596,0.661,7.283,0.661,10.88,0h2.133h2.773
			l3.627-1.92c2.021-1.098,3.887-2.461,5.547-4.053l163.2-163.2C426.97,285.665,427.04,267.97,416.17,257.013z"
              />
            </g>
          </g>
        </svg>
      ),
      isDisable: false,
      title: 'down',
      action: 'down',
      varient: 'primary',
    },
    {
      dataField: 'delete',
      handler: removeProcessHandler,
      icon: (
        <svg
          version="1.1"
          id="Capa_1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 466.205 466.205"
          fill="currentColor"
          width="16px"
        >
          <path d="m194.800781 164.769531 128.210938-128.214843c8.34375-8.339844 8.34375-21.824219 0-30.164063-8.339844-8.339844-21.824219-8.339844-30.164063 0l-128.214844 128.214844-128.210937-128.214844c-8.34375-8.339844-21.824219-8.339844-30.164063 0-8.34375 8.339844-8.34375 21.824219 0 30.164063l128.210938 128.214843-128.210938 128.214844c-8.34375 8.339844-8.34375 21.824219 0 30.164063 4.15625 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921875-2.089844 15.082031-6.25l128.210937-128.214844 128.214844 128.214844c4.160156 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921874-2.089844 15.082031-6.25 8.34375-8.339844 8.34375-21.824219 0-30.164063zm0 0" />
        </svg>
      ),
      isDisable: false,
      title: 'delete',
      action: 'delete',
      varient: 'danger-new',
    },
  ];

  //   useEffect(() => {
  //   if (showModal) {
  //     // Delay to allow modal + Formik to fully render
  //     setTimeout(() => {
  //       bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  //     }, 300); // adjust timing if needed
  //   }
  // }, [showModal, props.initialValues]);

  // Function to handle input changes
  const handleInputChange = (rowIndex, value) => {
    setSelectedProcesses((prevProcesses) =>
      prevProcesses.map((process, index) =>
        index === rowIndex ? { ...process, DueDates: value } : process
      )
    );
    validateDueDates(selectedProcesses);

    // if (selectedProcesses.length > 1) {
    //   for (let i = 0; i < selectedProcesses.length - 1; i++) {
    //     if (selectedProcesses[i].DueDates < value) {
    //       console.error(
    //         `DueDates are not in decreasing order. Error at index ${i + 1}, Id: ${selectedProcesses[i + 1].Id}`
    //       );
    //       return; // Exit once an error is found
    //     }
    //   }
    // }
  };

  return (
    <>
      <Modal
        // className="correction-modal fancy-modal"
        show={true}
        centered
        onHide={() => {}}
        size="xl"
        className="template-modal"
      >
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="add_process_template" />
            ) : (
              <FormattedMessage id="update_process_template" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.isLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>

        <Formik
          innerRef={formikRef}
          initialValues={{ ...props.initialValues, SelectedProcesses: 0 }}
          enableReinitialize
          validationSchema={ProcessTemplateValidateSchema}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
          validate={(values) => {
            const errors: any = {};
            if (!validateDueDates(selectedProcesses)) {
              errors.selectedProcesses = formatMessage(
                'due_dates_should_be_in_decreasing_order'
              );
            }
            return errors;
          }}
          onSubmit={async (values) => {
            const updatedProcesses = selectedProcesses.map((process, index) => {
              const filter =
                props.allProcess.filter((p) => p.Id === process.ProcessId)[0] ||
                process.Type;
              return {
                ...process,
                ProcessOrder: index + 1,
                Type: ProcessTypeEnum[filter.Type],
                ShortName: process.ShortName,
              };
            });
            props.submitHandler({
              ...values,
              TenantId: Number(getTenantIdFromLocalStorage()),
              ProcesTemplates: updatedProcesses,
            });
          }}
        >
          {({ dirty, errors, values }) => {
            return (
              <Form className="user-form">
                {props.isLoading ? (
                  <FormLoader loading={props.isLoading} />
                ) : null}
                <Modal.Body>
                  <div className="row g-4">
                    <div className="col-5">
                      <div className="form-group">
                        <Label
                          className="label-style col-form-label"
                          required={true}
                          label="template_name"
                        />
                        <Field
                          data-testid="testTemplateName"
                          className="form-control"
                          name="TemplateName"
                          type="text"
                          defaultValue={undefined}
                          placeholder=""
                          as={FormikFormInput}
                          maxLength={TEMPLATE_NAME_MAX_LENGTH}
                          validcharacter={validAllowedCharacters}
                          // disabled={props.id > 0}
                        />
                        {String(values.TemplateName || '').length >=
                          TEMPLATE_NAME_MAX_LENGTH && (
                          <div className="invalid-feedback d-block errorMessage">
                            Maximum Character Limit Reached [256]
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-0">
                        <Label
                          className="label-style col-form-label"
                          required={false}
                          label="process_template_remarks"
                        />
                        <Field
                          id="Remarks"
                          className="form-control process-textarea"
                          name="Remarks"
                          placeholder=""
                          type="text"
                          as={FormikFormTextArea}
                        />
                      </div>
                    </div>
                    <div className="col-7">
                      <div className="form-group">
                        <label className="label-style col-form-label">
                          <FormattedMessage id="select_process" />
                        </label>

                        <Select  styles={customStyles}
                          className="select-did-process"
                          classNames={{
                            option: (d) =>
                              `our-option ${d.isSelected ? 'active' : ''}`,
                          }}
                          options={selectedProcessesOptions}
                          value={[
                            {
                              label: formatMessage('please_select'),
                              value: '-1',
                            },
                          ]}
                          onChange={handleSelectChange}
                        />
                        <Field
                          data-testid="testSelectedProcesses"
                          className="form-control d-none"
                          name="SelectedProcesses"
                          type="text"
                          placeholder=""
                          as={FormikFormInput}
                          disabled={props.id > 0}
                        />
                      </div>
                      <div>
                        {errors.selectedProcesses &&
                        !validateDueDates(selectedProcesses) ? (
                          <div className="text-danger">
                            {typeof errors.selectedProcesses === 'string'
                              ? errors.selectedProcesses
                              : String(errors.selectedProcesses)}
                          </div>
                        ) : (
                          <div>{''}</div>
                        )}
                      </div>

                      {
                        <div className="tableDesign row-eq-height tableDevices changingPanel processTable check-item-list border rounded bg-white mt-4">
                          <div className="table-responsive">
                            <div className="react-bootstrap-table">
                              <table className="table">
                                <thead>
                                  <tr>
                                    {processHeaders.map((item, index) => {
                                      return (
                                        <th key={index}>
                                          {formatMessage(item)}
                                          {item ===
                                            'process_template_due_date_header_text' && (
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip
                                                  id={`must_be_less_than_or_equal_to_99`}
                                                >
                                                  {formatMessage(
                                                    'must_be_less_than_or_equal_to_99'
                                                  )}
                                                </Tooltip>
                                              }
                                            >
                                              <span className="normal-info-svg">
                                                <InfoCircle size={14} />
                                              </span>
                                            </OverlayTrigger>
                                          )}
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedProcesses &&
                                    selectedProcesses.map(
                                      (process, rowIndex) => (
                                        <tr key={rowIndex}>
                                          {/* <td>{process.ProcessId}</td> */}
                                          <td
                                            className="show-Elipses"
                                            title={process.ProcessName}
                                          >
                                            {process.ProcessName}
                                          </td>
                                          <td
                                            className="show-Elipses"
                                            title={process.ShortName}
                                          >
                                            {process.ShortName}
                                          </td>
                                          <td>
                                            <input
                                              className="form-control"
                                              type="number"
                                              min={0}
                                              max={99}
                                              value={process?.DueDates}
                                              onInvalid={(e) =>
                                                (
                                                  e.target as HTMLInputElement
                                                ).setCustomValidity(
                                                  formatMessage(
                                                    'max_allowed_value_99'
                                                  )
                                                )
                                              }
                                              onInput={(e) =>
                                                (
                                                  e.target as HTMLInputElement
                                                ).setCustomValidity('')
                                              }
                                              onChange={(e) =>
                                                handleInputChange(
                                                  rowIndex,
                                                  parseFloat(e.target.value) ||
                                                    0
                                                )
                                              }
                                            />
                                          </td>
                                          <td>
                                            {
                                              <div className="d-flex">
                                                {actionIcons.map(
                                                  (
                                                    action: IActionFormatter,
                                                    index: number
                                                  ) => {
                                                    return (
                                                      <button
                                                        className={`btn btn-light-${action.varient} btn-sm `}
                                                        key={index}
                                                        title={formatMessage(
                                                          action.title
                                                        )}
                                                        type="button"
                                                        disabled={
                                                          (action.dataField ===
                                                            'up' &&
                                                            rowIndex === 0) ||
                                                          (action.dataField ===
                                                            'down' &&
                                                            rowIndex ===
                                                              selectedProcesses.length -
                                                                1)
                                                        }
                                                        onClick={(event) =>
                                                          action.handler(
                                                            rowIndex
                                                          )
                                                        }
                                                      >
                                                        {action.icon}
                                                      </button>
                                                    );
                                                  }
                                                )}
                                              </div>
                                            }
                                          </td>
                                        </tr>
                                      )
                                    )}
                                 <div ref={bottomRef} />
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </Modal.Body>

                <Modal.Footer>
                  <div className="d-flex justify-content-center ">
                    <div className="row tab-action g-0">
                      <div className="col-12">
                        <Button
                          variant="light-danger"
                          type="button"
                          onClick={props.onModalClosed}
                          disabled={props.isLoading}
                        >
                          <FormattedMessage id="cancel" />
                        </Button>
                        <LoaderButton
                          type="submit"
                          isLoading={props.isLoading}
                          text="button_save"
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default AddEditProcessTemplateForm;
